import React, { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import ExpenseList from '../components/expenses/ExpenseList';
import ExpenseForm from '../components/expenses/ExpenseForm';
import RestaurantForm from '../components/restaurants/RestaurantForm'; // Re-introducing RestaurantForm
import { useExpenses } from '../hooks/useExpenses';
import { useSuggestions } from '../hooks/useSuggestions';
import { useRestaurants } from '../hooks/useRestaurants';
import { useToast } from '../hooks/useToast';
import { useUser } from '../hooks/useUser';
import { useAdmin } from '../hooks/useAdmin';
import { Expense, ExpenseCreate } from '../types/expense';
// import { Restaurant } from '../types/api'; // No longer directly used

const ExpensesPage: React.FC = () => {
  // Получаем telegram_id для сохранения выбора ресторана в БД
  const { telegramId } = useUser();
  
  // Проверяем права администратора
  const { isAdmin } = useAdmin(telegramId);
  
  const { 
    expenses, 
    loading, 
    error, 
    fetchExpenses, 
    createExpense, 
    updateExpense, 
    syncWithGoogleSheets
  } = useExpenses();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isRestaurantFormOpen, setIsRestaurantFormOpen] = useState(false); // Re-introducing state
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [syncingExpenseId, setSyncingExpenseId] = useState<string | null>(null);
  const [updatingExpenseId, setUpdatingExpenseId] = useState<string | null>(null);
  const [lastLoadedDate, setLastLoadedDate] = useState<string | null>(null);
  
  // Используем БД + localStorage для сохранения выбора ресторана
  const { restaurants, selectedRestaurantId, setSelectedRestaurantId, fetchRestaurantSuggestions } = useRestaurants({
    telegramId,
    autoLoadLastRestaurant: true
  });
  const { toasts, hideToast, success, error: showError } = useToast();
  
  // Используем предложения из API (передаём ID выбранного ресторана)
  const { suggestions: articles } = useSuggestions({ restaurantId: selectedRestaurantId || undefined });

  useEffect(() => {
    if (selectedRestaurantId) {
      // Бэкенд ЯВНО фильтрует по сегодняшней дате (не нужно передавать expense_date)
      const today = new Date().toISOString().split('T')[0];
      
      // Проверяем, нужно ли обновить данные (сменился ли день)
      if (lastLoadedDate && lastLoadedDate !== today) {
        console.log(`День сменился: ${lastLoadedDate} -> ${today}. Обновляем расходы.`);
      }
      
      fetchExpenses({ 
        restaurant_id: selectedRestaurantId
      });
      
      // Сохраняем дату последней загрузки
      setLastLoadedDate(today);
    }
  }, [selectedRestaurantId, fetchExpenses, lastLoadedDate]); // Re-fetch when restaurant changes or fetchExpenses changes


  useEffect(() => {
    fetchRestaurantSuggestions();
  }, []); // Only fetch restaurant suggestions once on mount

  // Автоматическая проверка смены дня при фокусе на странице
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedRestaurantId) {
        // Проверяем, сменился ли день
        const today = new Date().toISOString().split('T')[0];
        
        if (lastLoadedDate && lastLoadedDate !== today) {
          console.log('Обнаружена смена дня, перезагружаем расходы');
          fetchExpenses({ 
            restaurant_id: selectedRestaurantId
          });
          setLastLoadedDate(today);
        }
      }
    };

    // Проверка каждую минуту для надежного обнаружения смены дня
    const interval = setInterval(() => {
      if (selectedRestaurantId) {
        const today = new Date().toISOString().split('T')[0];
        
        if (lastLoadedDate && lastLoadedDate !== today) {
          console.log('Обнаружена смена дня (по таймеру), перезагружаем расходы');
          fetchExpenses({ 
            restaurant_id: selectedRestaurantId
          });
          setLastLoadedDate(today);
        }
      }
    }, 60000); // Проверяем каждую минуту

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [selectedRestaurantId, lastLoadedDate, fetchExpenses]);

  const handleCreateExpense = async (data: ExpenseCreate) => {
    if (!selectedRestaurantId) {
      showError('Выберите ресторан для создания расхода');
      return;
    }
    
    try {
      await createExpense({ 
        ...data, 
        user_id: 'user_001',
        restaurant_id: selectedRestaurantId // Используем выбранный ресторан
      });
      setIsFormOpen(false);
      
      // Показываем сообщение о сохранении (статус синхронизации виден на карточке)
      success('Расход сохранён');
    } catch (error) {
      console.error('Ошибка создания расхода:', error);
      showError('Ошибка при создании расхода');
    }
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setIsFormOpen(true);
  };

  const handleUpdateExpense = async (data: ExpenseCreate) => {
    if (editingExpense) {
      try {
        // Показываем индикатор обновления
        setUpdatingExpenseId(editingExpense.expense_id);
        
        // Обновляем расход (бэкенд автоматически синхронизирует)
        await updateExpense(editingExpense.expense_id, data);
        
        success('Расход успешно обновлён и синхронизирован!');
        setEditingExpense(null);
        setIsFormOpen(false);
      } catch (error) {
        console.error('Ошибка обновления расхода:', error);
        
        // Простое сообщение об ошибке
        showError('Цифры не поменялись. Попробуйте еще раз.');
        
        // Форма остается открытой, чтобы пользователь мог попробовать еще раз
      } finally {
        // Убираем индикатор обновления
        setUpdatingExpenseId(null);
      }
    }
  };


  const handleSyncExpense = async (expense: Expense) => {
    try {
      setSyncingExpenseId(expense.expense_id);
      await syncWithGoogleSheets(expense.expense_id);
      success('Расход успешно синхронизирован с Google Sheets!');
      
      // Обновляем список расходов после синхронизации
      if (selectedRestaurantId) {
        await fetchExpenses({ 
          restaurant_id: selectedRestaurantId
        });
      }
    } catch (error) {
      console.error('Ошибка синхронизации расхода:', error);
      showError(`Ошибка синхронизации: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setSyncingExpenseId(null);
    }
  };


  const handleCreateRestaurant = async (data: { name: string; googleSheetUrl: string }) => {
    try {
      const response = await fetch('/api/restaurants/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        // Пытаемся распарсить JSON с ошибкой от сервера
        try {
          const errorData = await response.json();
          const errorMessage = errorData.error || 'Неизвестная ошибка';
          throw new Error(errorMessage);
        } catch (parseError) {
          // Если не удалось распарсить JSON, используем общее сообщение
          throw new Error(`Ошибка сервера (${response.status})`);
        }
      }

      const result = await response.json();
      console.log('Restaurant created:', result);
      success('Ресторан успешно добавлен!');
      
      await fetchRestaurantSuggestions(); // Use fetchRestaurantSuggestions from useRestaurants hook
      setIsRestaurantFormOpen(false);
    } catch (error) {
      console.error('Ошибка создания ресторана:', error);
      showError(error instanceof Error ? error.message : 'Неизвестная ошибка');
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingExpense(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Toast уведомления */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}
      
      <Header
        // title="Управление расходами" // Removed title
        restaurants={restaurants}
        selectedRestaurantId={selectedRestaurantId}
        onSelectRestaurant={setSelectedRestaurantId}
        onAddRestaurantClick={() => setIsRestaurantFormOpen(true)}
        isAdmin={isAdmin}
      />
      
      <div className="px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        {!selectedRestaurantId ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Выберите ресторан чтобы посмотреть расходы</p>
          </div>
        ) : (
          <ExpenseList
            expenses={expenses}
            loading={loading}
            onEdit={handleEditExpense}
            onSync={handleSyncExpense}
            syncingExpenseId={syncingExpenseId}
            updatingExpenseId={updatingExpenseId}
          />
        )}
      </div>

      {/* Кнопка добавления расхода внизу - показываем только если выбран ресторан */}
      {selectedRestaurantId && (
        <div className="fixed bottom-24 left-0 right-0 px-4">
          <Button 
            onClick={() => setIsFormOpen(true)} 
            className="w-full flex items-center justify-center space-x-2 py-3 bg-green-600 hover:bg-green-700 shadow-md hover:shadow-lg"
          >
            <PlusIcon className="w-5 h-5" />
            <span style={{fontFamily: "'Times New Roman', Times, serif"}}>Добавить расход</span>
          </Button>
        </div>
      )}

       <ExpenseForm
         isOpen={isFormOpen}
         onClose={handleCloseForm}
         onSubmit={editingExpense ? handleUpdateExpense : handleCreateExpense}
         restaurants={restaurants}
         articles={articles}
         selectedRestaurantId={selectedRestaurantId || undefined}
        isEditing={!!editingExpense}
        initialData={editingExpense ? {
          article: editingExpense.article,
          amount: editingExpense.amount,
          comment: editingExpense.comment,
          restaurant_id: editingExpense.restaurant_id,
          user_id: 'user_001'
        } : undefined}
       />

      <RestaurantForm
        isOpen={isRestaurantFormOpen}
        onClose={() => setIsRestaurantFormOpen(false)}
        onSubmit={handleCreateRestaurant}
      />
    </div>
  );
};

export default ExpensesPage;
